import { useCallback, useEffect, useRef } from "react";
import { getScrollParent } from "./getScrollParent";

export const useAnchorScrollHandler = hash => {
  const anchorListenerCache = useRef({});
  const newContentListener = useRef();
  const anchorTargetScrollParent = useRef();
  const scrollingToElement = useRef(false);

  const clearListeners = useCallback(({ force = false } = {}) => {
    // If we're currently scrolling the element into view, we don't want to remove the
    // listeners as they're meant to listen to the user's scroll, and not to the
    // programmatic scroll.
    //
    // The force flag is meant to be used on component unmounting as it doesn't depend on
    // the current scrolling state.
    if (!force && scrollingToElement.current) return;

    if (newContentListener.current) {
      window.removeEventListener("newContentLoaded", newContentListener.current);
      newContentListener.current = null;
    }

    if (anchorTargetScrollParent.current) {
      anchorTargetScrollParent.current.removeEventListener("scroll", clearListeners);
      anchorTargetScrollParent.current = null;
    }
  }, []);

  // When the component is unmounted, we won't need the listeners anymore, so clear them
  // to avoid memory leaks or other strange behaviors.
  useEffect(() => {
    return () => clearListeners({ force: true });
  }, [clearListeners]);

  // This allows scrolling to an anchor when the content is loaded, and the anchor is
  // present in the current page.
  //
  // Note that it also waits for multiple "newContentLoaded" events to ensure that the
  // actual page content is loaded before trying to find the element in the page. This
  // especially allows accessing anchors when the page is first loaded, as there are
  // many "newContentLoaded" events for each part of the page that is loaded.
  //
  const scrollToAnchor = useCallback(() => {
    // Ensure that no listener for new content is still active before starting to process
    // a new visit's anchor scrolling.
    clearListeners();

    const element = hash && document.querySelector(hash);

    if (element) {
      const scrollElementIntoView = () => {
        scrollingToElement.current = true;
        // We use a setTimeout here to allow the browser to render the content before
        // scrolling to the element. This is useful when images are loaded as if we
        // don't wait for the end of the current JS thread, the element can be out of
        // the view or misplaced.
        setTimeout(() => {
          element.scrollIntoView({ behavior: "instant" });

          // Let the scroll happen before releasing the lock on scrollingToElement. Note
          // that the timeout is set to 10ms to ensure that the scroll is done before,
          // and that a value of 0ms isn't enough.
          setTimeout(() => (scrollingToElement.current = false), 10);
        }, 0);
      };
      // If the element is found, scroll it into view as soon as the page is loaded.
      scrollElementIntoView();

      // Whenever a new content is loaded into the DOM, we retry scroll the element
      // into the view. This allows handling the case where there are images that are
      // loaded after the initial content, and that change the page's height, which
      // can cause the element to be out of the view or misplaced.
      //
      // Note that as soon as the user scrolls the page, the newContentLoaded listener
      // is removed, and the scroll is not triggered anymore, to avoid conflicting
      // scroll issues with the user's interactions.
      newContentListener.current = scrollElementIntoView;
      window.addEventListener("newContentLoaded", newContentListener.current);

      const scrollParent = getScrollParent(element);
      // Bind the scroll listener to the scroll parent of the element, which is either
      // the window or a scrollable div.
      anchorTargetScrollParent.current =
        scrollParent.tagName === "HTML" ? window : scrollParent;

      anchorTargetScrollParent.current.addEventListener("scroll", clearListeners);

      // If the element was found and the scroll was triggered, or if the hash is empty,
      // we can remove the listener and the cache entry.
      if (element || !hash) {
        delete anchorListenerCache.current[hash];
        return window.removeEventListener("newContentLoaded", scrollToAnchor);
      }
    }

    // Avoid adding the same listener multiple times which causes scrolling issues with
    // modal openings.
    if (hash && !anchorListenerCache.current[hash]) {
      anchorListenerCache.current[hash] = scrollToAnchor;
      window.addEventListener("newContentLoaded", scrollToAnchor);
    }
  }, [clearListeners, hash]);

  return scrollToAnchor;
};
